import { formatDistanceToNow, parseISO } from "date-fns";
import { ChevronRight, Loader2, RefreshCw } from "lucide-react";

import { ReactComponent as ReportQASparkle } from "@assets/svg/report-qa-sparkle.svg";

import { Button } from "@components/ui/button";

import { useRunReportQAMutation } from "@hooks/mutations/use-report-qa";
import { useLatestReportQA } from "@hooks/queries/use-report-qa";

import { cn } from "@utils/ui";

import { OverallScoreDonut, severityToToneClass } from "./OverallScoreDonut";
import { SectionCard } from "./SectionCard";

/**
 * Right-rail panel for Report QA on the Report Builder page.
 *
 * Self-contained: only needs `templateId` + `caseNumber` so it can be
 * lifted into the new master-page right-panel container once that
 * lands. Lives outside the Report Builder folder so the move is a
 * one-line import change.
 *
 * States:
 *  - Loading: skeleton placeholders matching the final layout
 *  - Empty (no QA run yet): primary CTA "Run Report QA"
 *  - Loaded: header + donut + per-section cards + "Run QA again"
 *  - Error: terse failure with retry affordance
 */
type Props = {
  templateId: string;
  caseNumber: string;
  onClose?: () => void;
};

export const ReportQAPanel = ({ templateId, caseNumber, onClose }: Props) => {
  const {
    data: latest,
    isLoading,
    isError,
    refetch,
  } = useLatestReportQA({ templateId, caseNumber });

  const runQa = useRunReportQAMutation({ templateId, caseNumber });

  const handleRun = () => {
    // Clear any prior mutation error so the loading state renders cleanly
    // on retry (otherwise the switch below would still hit the error branch
    // for the brief moment before isPending flips on).
    runQa.reset();
    runQa.mutate();
  };

  // MDRS-92: surface mutation errors in the panel. Previously runQa.isError
  // was never checked, so any QA failure (no draft saved, network error,
  // function-app 502, etc.) was invisible — the loading state cleared and
  // the panel silently fell back to its empty or last-known state.
  const mutationErrorMessage = parseMutationError(runQa.error);

  return (
    <aside className="flex h-full flex-col gap-3 overflow-y-auto rounded-xl border border-slate-200 bg-background-white p-4 shadow-sm dark:border-dark-50">
      <Header onClose={onClose} />

      {isLoading || runQa.isPending ? (
        <LoadingState />
      ) : runQa.isError ? (
        <ErrorState
          description={
            mutationErrorMessage || "Couldn't run Report QA. Try again in a moment."
          }
          onRetry={handleRun}
        />
      ) : isError ? (
        <ErrorState
          description="Couldn't load the latest Report QA. Try again in a moment."
          onRetry={() => refetch()}
        />
      ) : !latest ? (
        <EmptyState onRun={handleRun} isRunning={runQa.isPending} />
      ) : (
        <LoadedState latest={latest} onRun={handleRun} isRunning={runQa.isPending} />
      )}
    </aside>
  );
};

/**
 * The API client throws `Error("Failed to run Report QA: <json>")` for
 * non-2xx responses where ``<json>`` is the stringified ``{ data, error }``
 * tuple. Pull the human-readable ``detail`` out of that so the user sees
 * the actual backend message ("save the report first", etc.) rather than
 * raw JSON.
 */
const parseMutationError = (error: Error | null): string | null => {
  if (!error) return null;
  const match = error.message.match(/Failed to run Report QA: (.+)$/);
  if (!match) return error.message;
  try {
    const parsed = JSON.parse(match[1]);
    if (parsed && typeof parsed === "object") {
      const detail = (parsed as { detail?: unknown }).detail;
      if (typeof detail === "string") return detail;
      const errorField = (parsed as { error?: { detail?: unknown } }).error;
      if (errorField && typeof errorField.detail === "string")
        return errorField.detail;
    }
  } catch {
    // Not JSON — fall through to the raw message.
  }
  return match[1];
};

const Header = ({ onClose }: { onClose?: () => void }) => (
  <div className="flex items-start justify-between gap-2">
    <div className="flex items-center gap-1.5">
      <ReportQASparkle
        className="h-[18px] w-[21px] text-[#0066CC]"
        aria-hidden="true"
      />
      <h3 className="text-[14px] font-semibold leading-5 text-[#1A1939]">
        Report QA
      </h3>
    </div>
    {onClose && (
      <button
        type="button"
        onClick={onClose}
        aria-label="Close Report QA panel"
        className="-m-0.5 inline-flex p-0.5 text-[#94A3B8] hover:text-[#1A1939]"
      >
        <ChevronRight className="size-4" />
      </button>
    )}
  </div>
);

const Description = () => (
  <p className="text-[12px] font-normal leading-4 text-[#66657A]">
    AI-generated suggestions to help improve your report against medicolegal
    reporting standards and template requirements.
  </p>
);

const RunButton = ({
  onRun,
  isRunning,
  label,
}: {
  onRun: () => void;
  isRunning: boolean;
  label: string;
}) => (
  <Button
    onClick={onRun}
    disabled={isRunning}
    className="h-[29px] gap-2.5 rounded-full border-0 bg-[#0066CC] px-[14px] py-1.5 text-[11px] font-semibold leading-[16.5px] tracking-[0.06px] text-white hover:bg-[#0066CC]/90"
  >
    {isRunning && <RefreshCw className="size-3 animate-spin" />}
    {label}
  </Button>
);

const LoadingState = () => (
  <div className="flex flex-col gap-3">
    <Description />
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16">
      <Loader2
        className="size-12 animate-spin text-primary"
        strokeWidth={2.5}
        aria-hidden="true"
      />
      <div className="flex flex-col items-center gap-1 text-center">
        <p className="text-sm font-semibold text-foreground">Running QA check...</p>
        <p className="text-muted-foreground text-xs">
          Analysing each section against reporting standards and template
          requirements.
        </p>
      </div>
    </div>
  </div>
);

const ErrorState = ({
  description,
  onRetry,
}: {
  description: string;
  onRetry: () => void;
}) => (
  <div className="flex flex-col gap-3">
    <Description />
    <p className="text-sm text-destructive">{description}</p>
    <Button size="sm" variant="outline" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

const EmptyState = ({
  onRun,
  isRunning,
}: {
  onRun: () => void;
  isRunning: boolean;
}) => (
  <div className="flex flex-col gap-3">
    <Description />
    <p className="text-muted-foreground text-xs">
      No QA pass has been run for this draft yet. Run a pass to see suggestions here.
    </p>
    <div>
      <RunButton onRun={onRun} isRunning={isRunning} label="Run Report QA" />
    </div>
  </div>
);

const LoadedState = ({
  latest,
  onRun,
  isRunning,
}: {
  latest: NonNullable<ReturnType<typeof useLatestReportQA>["data"]>;
  onRun: () => void;
  isRunning: boolean;
}) => {
  const evaluations = latest.section_evaluations_json ?? [];
  const lastUpdated = (() => {
    try {
      return formatDistanceToNow(parseISO(latest.created_at), { addSuffix: true });
    } catch {
      return null;
    }
  })();

  return (
    <div className="flex flex-col gap-3">
      <Description />

      <div className="flex items-center gap-2.5">
        <RunButton onRun={onRun} isRunning={isRunning} label="Run QA again" />
        {lastUpdated && (
          <span className="whitespace-nowrap text-[10px] font-normal leading-none text-[#94A3B8]">
            Last Updated: {lastUpdated}
          </span>
        )}
      </div>

      {(latest.overall_score != null || latest.severity_overall) && (
        <div className="flex flex-col gap-2 border-t border-slate-200 pt-3">
          <span className="text-[11px] font-semibold leading-[16.5px] text-[#66657A]">
            Overall Report Quality
          </span>
          <div className="flex items-start gap-3">
            <OverallScoreDonut
              score={latest.overall_score ?? 0}
              severityLabel={latest.severity_overall}
              evaluations={evaluations}
            />
            <div className="flex flex-1 flex-col gap-1">
              {latest.severity_overall && (
                <span
                  className={cn(
                    "text-[14px] font-semibold leading-none",
                    severityToToneClass(
                      latest.severity_overall,
                      latest.overall_score ?? 0
                    )
                  )}
                >
                  {latest.severity_overall}
                </span>
              )}
              {latest.summary && (
                <p className="text-[12px] font-normal leading-[18px] text-[#6B7280]">
                  {latest.summary}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {evaluations.length > 0 ? (
        <div className="flex flex-col gap-2">
          {evaluations.map((evaluation, idx) => (
            <SectionCard
              key={evaluation.name}
              evaluation={evaluation}
              index={idx + 1}
            />
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">
          No section evaluations were returned for this run.
        </p>
      )}
    </div>
  );
};
